import React, { useState } from 'react';
import { AlertCircle } from 'lucide-react';
import FormModal from '@shared/ui/FormModal';
import api from '../lib/api';
import toast from 'react-hot-toast';

interface FeedbackFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  isPublic?: boolean;
}

const FEEDBACK_CATEGORIES = [
  'Bug Report',
  'Feature Request',
  'General Feedback',
  'Subscription',
  'Documentation',
  'Other',
];

const FeedbackFormModal: React.FC<FeedbackFormModalProps> = ({ isOpen, onClose, isPublic = false }) => {
  const [formData, setFormData] = useState({
    category: 'General Feedback',
    subject: '',
    message: '',
    // Public-only fields
    name: '',
    email: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Reset form when modal closes
  const handleClose = () => {
    setFormData({
      category: 'General Feedback',
      subject: '',
      message: '',
      name: '',
      email: '',
    });
    setErrors({});
    onClose();
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    // Public mode requires name and email
    if (isPublic) {
      if (!formData.name.trim()) {
        newErrors.name = 'Name is required';
      } else if (formData.name.length > 100) {
        newErrors.name = 'Name must be 100 characters or less';
      }

      if (!formData.email.trim()) {
        newErrors.email = 'Email is required';
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        newErrors.email = 'Please enter a valid email address';
      }
    }

    if (!formData.subject.trim()) {
      newErrors.subject = 'Subject is required';
    } else if (formData.subject.length > 200) {
      newErrors.subject = 'Subject must be 200 characters or less';
    }

    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
    } else if (formData.message.length > 5000) {
      newErrors.message = 'Message must be 5000 characters or less';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const endpoint = isPublic ? '/feedback/submit-public' : '/feedback/submit';
      const response = await api.post(endpoint, formData);

      if (response.data.success) {
        toast.success('Thank you for contacting us! We will get back to you soon.');
        handleClose();
      } else {
        toast.error(response.data.error || 'Failed to submit message');
      }
    } catch (error: unknown) {
      const errorMessage = error instanceof Error 
        ? error.message 
        : (error as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Failed to submit message. Please try again.';
      console.error('Error submitting feedback:', error);
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <FormModal
      isOpen={isOpen}
      title="Contact support"
      description="Get help or share feedback with our team"
      onClose={handleClose}
      onSubmit={handleSubmit}
      size="lg"
      submitLabel="Send message"
      isSubmitting={isSubmitting}
      isDirty={formData.message.trim().length > 0}
    >
      <div className="space-y-6">
              {/* Public mode: Name and Email fields */}
              {isPublic && (
                <>
                  {/* Name */}
                  <div>
                    <label
                      htmlFor="name"
                      className="block text-sm font-medium text-text-secondary dark:text-text-dark-secondary mb-2"
                    >
                      Your Name <span className="text-error-600 dark:text-error-400">*</span>
                    </label>
                    <input
                      type="text"
                      id="name"
                      value={formData.name}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        setFormData({ ...formData, name: e.target.value });
                        if (errors.name) {
                          setErrors({ ...errors, name: '' });
                        }
                      }}
                      placeholder="Enter your name"
                      className="input"
                      disabled={isSubmitting}
                      maxLength={100}
                    />
                    {errors.name && (
                      <div className="flex items-center gap-1 mt-2 text-sm text-error-600 dark:text-error-400">
                        <AlertCircle className="w-4 h-4" />
                        <span>{errors.name}</span>
                      </div>
                    )}
                  </div>

                  {/* Email */}
                  <div>
                    <label
                      htmlFor="email"
                      className="block text-sm font-medium text-text-secondary dark:text-text-dark-secondary mb-2"
                    >
                      Email Address <span className="text-error-600 dark:text-error-400">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      value={formData.email}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        setFormData({ ...formData, email: e.target.value });
                        if (errors.email) {
                          setErrors({ ...errors, email: '' });
                        }
                      }}
                      placeholder="Enter your email address"
                      className="input"
                      disabled={isSubmitting}
                    />
                    {errors.email && (
                      <div className="flex items-center gap-1 mt-2 text-sm text-error-600 dark:text-error-400">
                        <AlertCircle className="w-4 h-4" />
                        <span>{errors.email}</span>
                      </div>
                    )}
                  </div>
                </>
              )}

              {/* Category */}
              <div>
                <label
                  htmlFor="category"
                  className="block text-sm font-medium text-text-secondary dark:text-text-dark-secondary mb-2"
                >
                  Category <span className="text-error-600 dark:text-error-400">*</span>
                </label>
                <select
                  id="category"
                  value={formData.category}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                    setFormData({ ...formData, category: e.target.value })
                  }
                  className="input"
                  disabled={isSubmitting}
                >
                  {FEEDBACK_CATEGORIES.map((category) => (
                    <option key={category} value={category}>
                      {category}
                    </option>
                  ))}
                </select>
              </div>

              {/* Subject */}
              <div>
                <label
                  htmlFor="subject"
                  className="block text-sm font-medium text-text-secondary dark:text-text-dark-secondary mb-2"
                >
                  Subject <span className="text-error-600 dark:text-error-400">*</span>
                </label>
                <input
                  type="text"
                  id="subject"
                  value={formData.subject}
                  onChange={(e) => {
                    setFormData({ ...formData, subject: e.target.value });
                    if (errors.subject) {
                      setErrors({ ...errors, subject: '' });
                    }
                  }}
                  placeholder="Brief description of your feedback"
                  className="input"
                  disabled={isSubmitting}
                  maxLength={200}
                />
                {errors.subject && (
                  <div className="flex items-center gap-1 mt-2 text-sm text-error-600 dark:text-error-400">
                    <AlertCircle className="w-4 h-4" />
                    <span>{errors.subject}</span>
                  </div>
                )}
                <div className="mt-1 text-xs text-text-tertiary dark:text-text-dark-tertiary text-right">
                  {formData.subject.length}/200
                </div>
              </div>

              {/* Message */}
              <div>
                <label
                  htmlFor="message"
                  className="block text-sm font-medium text-text-secondary dark:text-text-dark-secondary mb-2"
                >
                  Message <span className="text-error-600 dark:text-error-400">*</span>
                </label>
                <textarea
                  id="message"
                  value={formData.message}
                  onChange={(e) => {
                    setFormData({ ...formData, message: e.target.value });
                    if (errors.message) {
                      setErrors({ ...errors, message: '' });
                    }
                  }}
                  placeholder="Please provide detailed feedback..."
                  rows={6}
                  className="input resize-none"
                  disabled={isSubmitting}
                  maxLength={5000}
                />
                {errors.message && (
                  <div className="flex items-center gap-1 mt-2 text-sm text-error-600 dark:text-error-400">
                    <AlertCircle className="w-4 h-4" />
                    <span>{errors.message}</span>
                  </div>
                )}
                <div className="mt-1 text-xs text-text-tertiary dark:text-text-dark-tertiary text-right">
                  {formData.message.length}/5000
                </div>
              </div>



      </div>
    </FormModal>
  );
};

export default FeedbackFormModal;
